import {
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import "./documentBuider.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InputSearch from "../Inputs/InputSearch";
import { reorderArray } from "../../helpers/utils/reorderArray";
import { documentBuilderVariants } from "../../enums/documentBuiderVariants";
import { SECTION_URL, SNIPPETS_URL } from "../../helpers/url_api_helper";

const MAX_VISIBLE_ITEMS = 10;

export const catalogItemTypes = {
  SNIPPET: "Snippet",
  SECTION: "Section",
};

const normalizeCatalogItems = (items = [], type) => {
  return items.map((item) => ({
    id: item.id,
    type,
    title: item.title,
    description: item.description ?? "",
    active: item.active,
  }));
};

const normalizeInputSectionItems = (items = []) => {
  return items
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      instanceId: crypto.randomUUID(),
      sourceId: String(item.idSnippet),
      sourceType: catalogItemTypes.SNIPPET,
      title: item.snippetTitle,
      order: item.order,
      pageBreak: false,
      active: item.active,
    }));
};

const normalizeInputDocumentItems = (items = []) => {
  return items
    .sort((a, b) => a.order - b.order)
    .map((item) => {
      const isSnippet = item.idSnippet !== null;

      return {
        instanceId: crypto.randomUUID(),
        sourceType: isSnippet ? "Snippet" : "Section",
        sourceId: isSnippet ? item.idSnippet : item.idSection,
        title: isSnippet ? item.snippetTitle : item.sectionTitle,
        order: item.order,
        pageBreak: Boolean(item.pageBreak),
        active: isSnippet ? item.snippetActive : item.sectionActive,
      };
    });
};

const normalizeInputItems = (variant, items = []) => {
  if (variant === documentBuilderVariants.SECTION) {
    return normalizeInputSectionItems(items);
  }

  if (variant === documentBuilderVariants.DOCUMENT) {
    return normalizeInputDocumentItems(items);
  }

  return [];
};

const normalizeOutputSectionItems = (items = []) => {
  return {
    content: items.map((item, index) => {
      const payload = {
        order: index,
        idSnippet: item.sourceId,
      };

      return payload;
    }),
  };
};

const normalizeOutputDocumentItems = (items = []) => {
  return {
    content: items.map((item, index) => {
      const payload = {
        order: index,
        type: item.sourceType,
        idSnippet:
          item.sourceType === catalogItemTypes.SNIPPET ? item.sourceId : null,
        idSection:
          item.sourceType === catalogItemTypes.SECTION ? item.sourceId : null,
        pageBreak: Boolean(item.pageBreak),
      };

      return payload;
    }),
  };
};

const normalizeOutputItems = (variant, items = []) => {
  if (variant === documentBuilderVariants.SECTION) {
    return normalizeOutputSectionItems(items);
  }

  if (variant === documentBuilderVariants.DOCUMENT) {
    return normalizeOutputDocumentItems(items);
  }
};

const CatalogItem = ({ item, onSelect, disabled }) => (
  <div
    className={`document-builder__catalog-item ${!item.active && "document-builder__catalog-item--inactive"}`}
  >
    <span>{item.title}</span>
    {!disabled && (
      <button
        type="button"
        className="document-builder__catalog-item-action"
        onClick={(e) => {
          e.preventDefault();
          onSelect(item);
        }}
        onKeyDown={(e) => {
          if(e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <i className="fas fa-plus-square" /> Select
      </button>
    )}
  </div>
);

const DocumentBuider = ({
  variant,
  loading = false,
  disabled = false,
  required = true,
  error = false,
  handleChange,
  initialSelectedItems = [],
}) => {
  const [activeTab, setActiveTab] = useState(variant === documentBuilderVariants.DOCUMENT ? 2 : 1);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const lastInitialRef = useRef(null);

  const catalogByTab = {
    1: snippets,
    2: sections,
  };

  const createDocumentItem = (item) => {
    return {
      instanceId: crypto.randomUUID(),
      sourceId: item.id,
      sourceType: item.type,
      title: item.title,
      pageBreak: false,
      active: item.active,
    };
  };

  const normalizeInitialItems = useCallback(
    (items) => normalizeInputItems(variant, items),
    [variant],
  );

  const selectItem = useCallback(
    (item) => {
      if (disabled) return;
      setSelectedItems((prev) => [...prev, createDocumentItem(item)]);
    },
    [disabled],
  );

  const removeItem = useCallback(
    (id) => {
      if (disabled) return;
      setSelectedItems((prev) => prev.filter((item) => item.instanceId !== id));
    },
    [disabled],
  );

  const reorderItem = (fromIndex, toIndex) => {
    if (disabled) return;
    if (toIndex < 0 || toIndex >= selectedItems.length) {
      return;
    }
    setSelectedItems((prev) => reorderArray(prev, fromIndex, toIndex));
  };

  const setPageBreak = (id, value) => {
    if (disabled) return;
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.instanceId === id ? { ...item, pageBreak: value } : item,
      ),
    );
  };

  const changeTab = (index) => {
    setActiveTab(index);
    setSearch("");
  };

  const filteredCatalog = useMemo(() => {
    const normalizedFilter = search.trim().toLowerCase();
    const data = catalogByTab[activeTab] ?? [];

    if (normalizedFilter.length < 3) {
      return data.slice(0, MAX_VISIBLE_ITEMS);
    }

    return data.filter((item) => {
      return (
        item.title.toLowerCase().includes(normalizedFilter) ||
        item.description.toLowerCase().includes(normalizedFilter)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTab, snippets, sections]);

  useEffect(() => {
    let mounted = true;

    const fetchCatalogData = async () => {
      setLoadingCatalog(true);
      setCatalogError(null);
      try {
        let snippetsRes;
        let sectionsRes;
        if (variant === documentBuilderVariants.SECTION) {
          snippetsRes = await SNIPPETS_URL.getSnippetAll();
        } else {
          [snippetsRes, sectionsRes] = await Promise.all([
            SNIPPETS_URL.getSnippetAll(),
            SECTION_URL.getSectionAll(),
          ]);
        }

        if (!mounted) {
          return;
        }
        if (variant === documentBuilderVariants.SECTION) {
          setSnippets(
            normalizeCatalogItems(snippetsRes, catalogItemTypes.SNIPPET),
          );
        } else {
          setSnippets(
            normalizeCatalogItems(snippetsRes, catalogItemTypes.SNIPPET),
          );
          setSections(
            normalizeCatalogItems(sectionsRes, catalogItemTypes.SECTION),
          );
        }
      } catch {
        setCatalogError("Error loading catalog");
      } finally {
        setLoadingCatalog(false);
      }
    };

    fetchCatalogData();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialSelectedItems) return;

    const normalized = normalizeInitialItems(initialSelectedItems);

    const serialized = JSON.stringify(normalized);

    if (serialized === lastInitialRef.current) {
      return;
    }

    lastInitialRef.current = serialized;
    setSelectedItems(normalized);
  }, [initialSelectedItems, normalizeInitialItems]);

  useEffect(() => {
    if (typeof handleChange !== "function") return;

    handleChange(normalizeOutputItems(variant, selectedItems));
  }, [selectedItems, variant, handleChange]);

  if (!Object.values(documentBuilderVariants).includes(variant)) {
    return null;
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Row className="document-builder">
      <Col xs={12} lg={6} className="document-builder__catalog">
        <Nav tabs>
          {variant === documentBuilderVariants.DOCUMENT && (
            <NavItem>
              <NavLink
                onClick={() => {
                  changeTab(2);
                }}
                className={activeTab === 2 ? "active" : ""}
              >
                Sections
              </NavLink>
            </NavItem>
          )}
          <NavItem>
            <NavLink
              onClick={(e) => {
                e.preventDefault();
                changeTab(1);
              }}
              className={activeTab === 1 ? "active" : ""}
            >
              Snippets
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={1}>
            {!disabled && (
              <InputSearch
                placeholderSearch={"Search by title or description"}
                searchValueFilter={search}
                setSearchValueFilter={setSearch}
              />
            )}
            <div className="document-builder__catalog-list">
              {catalogError !== null ? (
                catalogError
              ) : loadingCatalog ? (
                <div className="d-flex justify-content-center">
                  <Spinner color="primary" />
                </div>
              ) : filteredCatalog.length === 0 ? (
                "No items found"
              ) : (
                filteredCatalog.map((item) => (
                  <CatalogItem
                    key={item.id}
                    item={item}
                    onSelect={selectItem}
                    disabled={disabled}
                  />
                ))
              )}
            </div>
          </TabPane>
          {variant === documentBuilderVariants.DOCUMENT && (
            <TabPane tabId={2}>
              {!disabled && (
                <InputSearch
                  placeholderSearch={"Search by title or description"}
                  searchValueFilter={search}
                  setSearchValueFilter={setSearch}
                />
              )}
              <div className="document-builder__catalog-list">
                {catalogError !== null ? (
                  catalogError
                ) : loadingCatalog ? (
                  <div className="d-flex justify-content-center">
                    <Spinner color="primary" />
                  </div>
                ) : filteredCatalog.length === 0 ? (
                  "No items found"
                ) : (
                  filteredCatalog.map((item) => (
                    <CatalogItem
                      key={item.id}
                      item={item}
                      onSelect={selectItem}
                      disabled={disabled}
                    />
                  ))
                )}
              </div>
            </TabPane>
          )}
        </TabContent>
      </Col>
      <Col
        xs={12}
        lg={6}
        className={`document-builder__document ${error && "document-builder__document--error"}`}
      >
        {variant === documentBuilderVariants.SECTION && (
          <p className="document-builder__document-title">
            Select Snippets for this Section
          </p>
        )}
        {variant === documentBuilderVariants.DOCUMENT && (
          <p className="document-builder__document-title">
            Select Snippets and Sections for this Document
          </p>
        )}

        <div className="document-builder__document-wrapper">
          <div className="document-builder__document-list">
            {selectedItems.length === 0
              ? "No items selected"
              : selectedItems.map((item, index) => {
                  return (
                    <div
                      className={`document-builder__document-item ${
                        item.sourceType === catalogItemTypes.SECTION
                          ? "document-builder__document-item--section"
                          : "document-builder__document-item--snippet"
                      } ${(!item.active || disabled) && "document-builder__document-item--inactive"}`}
                      key={item.instanceId}
                    >
                      {!disabled && (
                        <div className="document-builder__document-item-ordenation-wrapper">
                          <button
                            type="button"
                            className={`document-builder__document-item-ordenation ${
                              index === selectedItems.length - 1
                                ? "disabled"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              reorderItem(index, index + 1);
                            }}
                          >
                            <i className="fas fa-caret-down"></i>
                          </button>
                          <button
                            type="button"
                            className={`document-builder__document-item-ordenation ${
                              index === 0 ? "disabled" : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              reorderItem(index, index - 1);
                            }}
                          >
                            <i className="fas fa-caret-up"></i>
                          </button>
                        </div>
                      )}
                      <div className="document-builder__document-item-properties">
                        <p className="document-builder__document-item-type">
                          {item.sourceType}
                        </p>
                        <p className="document-builder__document-item-title">
                          {item.title}
                        </p>
                        {variant === documentBuilderVariants.DOCUMENT && (
                          <label className="document-builder__document-item-page-break">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={item.pageBreak}
                              onChange={(e) => {
                                setPageBreak(item.instanceId, e.target.checked);
                              }}
                            />{" "}
                            Page break
                          </label>
                        )}
                      </div>
                      {!disabled && (
                        <div>
                          {" "}
                          <button
                            type="button"
                            disabled={disabled}
                            className="document-builder__document-item-action"
                            onClick={(e) => {
                              e.preventDefault();
                              removeItem(item.instanceId);
                            }}
                          >
                            <i className="fas fa-minus-square"></i>Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default DocumentBuider;
